import React, { useState, useEffect, useMemo } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useTranslation } from "react-i18next";


type RoomModalProps = {
    room: RoomsType;
    isOpen: boolean;
    onClose: () => void;
};

export type RoomsType = {
    gallery: { image: string }[];
    image: string;
    name: string;
    content: string;
    amenities: { icon: string }[];
    max_adults: number;
    max_children: number;
    size: string;
};

const RoomModal: React.FC<RoomModalProps> = ({ room, isOpen, onClose }) => {
    if (!isOpen) return null;

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
    const thumbnailsPerPage = 6; // Number of thumbnails to show at a time
    const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
    const { t } = useTranslation(["common", "validation", "glossary"]);

    console.log(room);

    const resetOverflow = () => {
        document.body.style.overflow = "auto";
    };

    useEffect(() => {
        const currentEndIndex = thumbnailStartIndex + thumbnailsPerPage;
        if (
            currentImageIndex < thumbnailStartIndex ||
            currentImageIndex >= currentEndIndex
        ) {
            const newStartIndex =
                Math.floor(currentImageIndex / thumbnailsPerPage) *
                thumbnailsPerPage;
            setThumbnailStartIndex(newStartIndex);
        }
    }, [currentImageIndex, thumbnailStartIndex, thumbnailsPerPage]);

    const slides = useMemo(() => {
        return room.gallery.length > 0
            ? room.gallery.map((item) => ({ src: item.image }))
            : [{ src: room.image }];
    }, [room.gallery, room.image]);

    const handlePrevImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : room.gallery.length - 1,
        );
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex < room.gallery.length - 1 ? prevIndex + 1 : 0,
        );
    };

    const handleThumbnailClick = (index: number) => {
        setCurrentImageIndex(index);
    };

    const handleBigImageClick = () => {
        setLightboxIndex(currentImageIndex);
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        // Cleanup on component unmount
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    return (
        <div className="fixed inset-0 flex items-start md:items-center justify-center bg-black bg-opacity-50 overflow-y-auto z-[100]">
            <div className="bg-white md:rounded-lg shadow-lg overflow-hidden max-w-full md:max-w-6xl w-full h-[calc(100vh-80px)] md:h-auto mt-20">
                <div className="relative pt-16 md:pt-6 p-5 md:p-6 h-full overflow-y-auto">
                    <button
                        onClick={() => {
                            resetOverflow();
                            onClose();
                        }}
                        className="absolute top-2 right-5 hover:text-gray-800 text-4xl md:text-3xl"
                    >
                        &times;
                    </button>
                    <div className="flex flex-col md:flex-row h-full">
                        <div className="flex-1 flex flex-col">
                            <div className="relative md:flex-none flex-1">
                                {room.gallery && room.gallery.length > 0 ? (
                                    <>
                                        <img
                                            src={
                                                room.gallery[currentImageIndex]
                                                    .image
                                            }
                                            alt=""
                                            className="w-full h-[300px] md:h-[400px] object-cover rounded-lg cursor-pointer"
                                            onClick={handleBigImageClick}
                                        />
                                        <button
                                            onClick={handlePrevImage}
                                            className="absolute left-2 opacity-90 md:top-[42%] top-[41%]  px-3 transform -translate-y-1/2 bg-[#1D26CA] p-2 rounded-full rotate-180"
                                        >
                                            <div className="text-white">
                                                &#10132;
                                            </div>
                                        </button>
                                        <button
                                            onClick={handleNextImage}
                                            className="absolute right-2 md:top-[42%] top-[41%] opacity-90 px-3 transform bg-[#1D26CA] -translate-y-1/2 p-2 rounded-full"
                                        >
                                            <div className="text-white">
                                                {" "}
                                                &#10132;
                                            </div>
                                        </button>
                                    </>
                                ) : (
                                    <img
                                        src={room.image}
                                        alt=""
                                        className="w-full h-[400px] md:h-[400px] object-cover rounded-lg"
                                    />
                                )}
                                {room.gallery && room.gallery.length > 0 && (
                                    <div className="mt-4 flex md:justify-start justify-center space-x-2 overflow-hidden">
                                        <div className="flex space-x-1 overflow-x-auto">
                                            {room.gallery
                                                .slice(
                                                    thumbnailStartIndex,
                                                    thumbnailStartIndex +
                                                        thumbnailsPerPage,
                                                )
                                                .map((imageObj, index) => (
                                                    <img
                                                        key={
                                                            thumbnailStartIndex +
                                                            index
                                                        }
                                                        src={imageObj.image}
                                                        alt={`Thumbnail ${thumbnailStartIndex + index + 1}`}
                                                        className={`w-[50px] md:w-[87px] h-[50px] rounded md:h-[76px] object-cover cursor-pointer ${
                                                            thumbnailStartIndex +
                                                                index ===
                                                            currentImageIndex
                                                                ? "border-2 border-[#1D26CA]"
                                                                : ""
                                                        }`}
                                                        onClick={() =>
                                                            handleThumbnailClick(
                                                                thumbnailStartIndex +
                                                                    index,
                                                            )
                                                        }
                                                    />
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 pl-0 md:pl-8">
                            <h1 className="text-2xl md:mt-0 mt-6 font-bold">
                                {room.name}
                            </h1>
                            <div className="mt-4 flex justify-start flex-wrap gap-2">
                                {room.amenities && room.amenities.length > 0 ? (
                                    room.amenities.map((amenity, index) => (
                                        <div
                                            key={index}
                                            className="text-primary border border-[#767687] font-medium flex items-center py-1.5 pl-1.5 pr-2 mr-2 rounded-[9px]"
                                        >
                                            <span
                                                className="flex mr-2 items-center"
                                                dangerouslySetInnerHTML={{
                                                    __html: amenity.icon,
                                                }}
                                            />
                                            <span className="text-black font-light">
                                                {amenity.name}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <span></span>
                                )}
                            </div>

                            <div className="mt-4">
                                <h3 className="text-xl mb-4 font-semibold">
                                    {t("info", { ns: "glossary" })}
                                </h3>
                                <div className="mt-6 md:hidden bock mb-6 md:mb-0 font-semibold">
                                    <p>{t("size", { ns: "glossary" })}
                                        {":    "} {room.size} <span>m2</span>
                                    </p>
                                    <p>
                                        {t("adults", { ns: "glossary" })}
                                        {":    "} {room.max_adults}
                                    </p>
                                    <p>{t("childs", { ns: "glossary" })}
                                        {":    "}{room.max_children}</p>
                                </div>
                                <div
                                    className="text-sm text-gray-600 mb-8 overflow-y-auto max-h-full md:max-h-[430px]"
                                    dangerouslySetInnerHTML={{
                                        __html: room.content,
                                    }}
                                ></div>
                            </div>
                            <div className="mt-4 hidden md:block mb-12 md:mb-0 font-semibold">
                                <p>
                                    {t("size", { ns: "glossary" })}
                                    {":    "} {room.size} <span>m2</span>
                                </p>
                                <p>
                                    {" "}
                                    {t("adults", { ns: "glossary" })}
                                    {":    "}
                                    {room.max_adults}
                                </p>
                                <p>
                                    {t("childs", { ns: "glossary" })}
                                    {":    "} {room.max_children}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Lightbox
                styles={{
                    container: {
                        backgroundColor: "rgba(1,1,1,0.9)",
                    },
                }}
                index={lightboxIndex}
                slides={slides}
                open={lightboxIndex >= 0}
                close={() => setLightboxIndex(-1)}
            />
        </div>
    );
};

export default RoomModal;
